/**
 * Markdown editor bootstrapper.
 *
 * Upgrades any `<textarea data-markdown>` into an EasyMDE instance with a
 * lightweight, opinionated toolbar (bold / italic / headings / lists / link /
 * preview / guide). The original <textarea> remains the source of truth for
 * form submission, so server-side code is unchanged.
 *
 * Alpine.js compatibility:
 *   When the textarea has `x-model`, EasyMDE's CodeMirror mirrors writes back
 *   to the underlying textarea. We additionally dispatch a native `input`
 *   event on every change so Alpine's reactive state (e.g. the char counter
 *   and `isStepValid()` on the posting wizard) stays in sync.
 *
 * Limits:
 *   If the textarea has a `maxlength` attribute, we honor it — further typing
 *   is trimmed to the limit and a char counter is rendered in the status bar.
 */

import EasyMDE from 'easymde';
import 'easymde/dist/easymde.min.css';

const TOOLBAR = [
    'bold',
    'italic',
    '|',
    'heading-2',
    'heading-3',
    '|',
    'unordered-list',
    'ordered-list',
    '|',
    'link',
    'quote',
    '|',
    'preview',
    'guide'
];

function buildStatus(maxLength) {
    const status = ['lines'];
    if (maxLength) {
        status.push({
            className: 'characters',
            defaultValue: (el) => {
                el.setAttribute('data-max', String(maxLength));
                el.innerHTML = `0 / ${maxLength}`;
            },
            onUpdate: (el) => {
                const cm = el.closest('.EasyMDEContainer')?.querySelector('.CodeMirror');
                const len = cm?.CodeMirror?.getValue().length ?? 0;
                el.innerHTML = `${len} / ${maxLength}`;
                el.classList.toggle('near-limit', len > maxLength * 0.9);
                el.classList.toggle('over-limit', len > maxLength);
            }
        });
    }
    return status;
}

function enforceMaxLength(mde, maxLength) {
    if (!maxLength) return;
    const cm = mde.codemirror;
    cm.on('beforeChange', (_instance, change) => {
        if (change.origin === 'setValue' || change.origin === 'ignore') return;

        const currentValue = cm.getValue();
        const removed = cm.getRange(change.from, change.to).length;
        const added = change.text.join('\n').length;
        const nextLen = currentValue.length - removed + added;

        if (nextLen <= maxLength) return;

        const allowed = Math.max(0, maxLength - (currentValue.length - removed));
        if (allowed <= 0) {
            change.cancel();
            return;
        }
        let remaining = allowed;
        const truncated = [];
        for (const line of change.text) {
            if (remaining <= 0) { truncated.push(''); continue; }
            if (line.length <= remaining) {
                truncated.push(line);
                remaining -= line.length;
            } else {
                truncated.push(line.slice(0, remaining));
                remaining = 0;
            }
        }
        change.update(change.from, change.to, truncated);
    });
}

function initEditor(textarea) {
    if (!textarea || textarea.dataset.mdInitialized === '1') return;
    textarea.dataset.mdInitialized = '1';

    const maxLengthAttr = textarea.getAttribute('maxlength');
    const maxLength = maxLengthAttr ? parseInt(maxLengthAttr, 10) : 0;

    const placeholder = textarea.getAttribute('placeholder') || '';
    const minHeight = textarea.dataset.markdownMinHeight || '180px';

    const mde = new EasyMDE({
        element: textarea,
        autoDownloadFontAwesome: true,
        spellChecker: false,
        status: buildStatus(maxLength),
        toolbar: TOOLBAR,
        placeholder,
        minHeight,
        forceSync: true,
        previewClass: ['editor-preview', 'markdown-body'],
        sideBySideFullscreen: false,
        renderingConfig: {
            singleLineBreaks: false
        }
    });

    enforceMaxLength(mde, maxLength);

    // Entry point for pages that need to set the value programmatically or refresh
    // the editor after it becomes visible (CodeMirror mis-measures while hidden).
    textarea.easyMDE = mde;

    const syncTextarea = () => {
        textarea.value = mde.value();
    };

    mde.codemirror.on('change', () => {
        syncTextarea();
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
    });
    mde.codemirror.on('blur', () => {
        syncTextarea();
        textarea.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

function initAll(root = document) {
    root.querySelectorAll('textarea[data-markdown]').forEach(initEditor);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
} else {
    initAll();
}

document.body?.addEventListener('htmx:afterSwap', (evt) => {
    initAll(evt.detail?.target || document);
});

window.initMarkdownEditors = initAll;
